import { parse } from 'svg-parser';
import { PolySpline } from '../components/shapes/PolySpline';
import { Rectangle } from '../components/shapes/Rectangle';
import { Circle } from '../components/shapes/Circle';
import { Ellipse } from '../components/shapes/Ellipse';

/* =========================
   CUBIC SAMPLER
========================= */

function sampleCubic(p0, cp1, cp2, p3, steps = 8) {
  const pts = [];

  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;

    const x =
      u * u * u * p0.x +
      3 * u * u * t * cp1.x +
      3 * u * t * t * cp2.x +
      t * t * t * p3.x;

    const y =
      u * u * u * p0.y +
      3 * u * u * t * cp1.y +
      3 * u * t * t * cp2.y +
      t * t * t * p3.y;

    pts.push({ x, y, type: 'interpolative' });
  }

  return pts;
}

function getThreePointsFromEllipse(cx, cy, rx, ry) {
  // P1 and P2 are the ends of the horizontal axis (major axis if rx > ry)
  const p1 = { x: cx - rx, y: cy };
  const p2 = { x: cx + rx, y: cy };

  // P3 is a point on the circumference. 
  // We use the top of the vertical axis (0, ry)
  const p3 = { x: cx, y: cy - ry };

  return [p1, p2, p3];
}
/* =========================
   MAIN FUNCTION
========================= */

function parseSvg(svgText, selectionArea, transform) {
  const parsed = parse(svgText);
  const shapes = [];

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let lastControlX = 0;
  let lastControlY = 0;
  let prevType = null;

  const numberRegex = /-?\d*\.?\d+(?:e[-+]?\d+)?/gi;

  /* =========================
     BOUNDS DETECTION
  ========================== */

  function findBounds(node) {
    if (!node) return;



    if (node.type === 'element' && node.properties) {
      const props = node.properties;


      if (node.tagName === 'ellipse' || node.tagName === 'circle') {
        const r_x = node.tagName === 'circle' ? parseFloat(node.properties.r) : parseFloat(node.properties.rx);
        const r_y = node.tagName === 'circle' ? parseFloat(node.properties.r) : parseFloat(node.properties.ry);
        const c_x = parseFloat(node.properties.cx);
        const c_y = parseFloat(node.properties.cy);

        minX = Math.min(minX, c_x - r_x);
        maxX = Math.max(maxX, c_x + r_x);
        minY = Math.min(minY, c_y - r_y);
        maxY = Math.max(maxY, c_y + r_y);
      }

      if (node.tagName === 'path' && props.d) {
        const commands = props.d.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g) || [];
        let currentX = 0;
        let currentY = 0;
        let startX = 0;
        let startY = 0;

        let im = 0;


        if (commands) {
          commands.forEach(command => {
            const type = command[0];
            const args =
              command.slice(1).match(numberRegex)?.map(Number) || [];

            let i = 0;

            if (type === 'z') {
              currentX = startX;
              currentY = startY;
            }

            while (i < args.length) {
              let nextLastControlX, nextLastControlY;
              switch (type) {
                case 'M':
                  currentX = args[i++];
                  currentY = args[i++];
                  startX = currentX;
                  startY = currentY;
                  break;

                case 'm':
                  currentX += args[i++];
                  currentY += args[i++];
                  startX = currentX;
                  startY = currentY;
                  break;

                case 'L':
                  currentX = args[i++];
                  currentY = args[i++];
                  break;

                case 'l':
                  currentX += args[i++];
                  currentY += args[i++];
                  break;

                case 'C':
                  currentX = args[i + 4];
                  currentY = args[i + 5];
                  i += 6;
                  break;

                case 'c':
                  currentX += args[i + 4];
                  currentY += args[i + 5];
                  i += 6;
                  break;

                case 'Z':
                case 'z':
                  currentX = startX;
                  currentY = startY;
                  i = args.length;
                  break;
                case 'S':
                case 's': {
                  let x2 = args[i++];
                  let y2 = args[i++];
                  let x = args[i++];
                  let y = args[i++];

                  if (type === 's') {
                    x2 += currentX;
                    y2 += currentY;
                    x += currentX;
                    y += currentY;
                  }

                  // Calculate the mirrored reflection of the previous control point
                  let cp1X, cp1Y;
                  if (['C', 'c', 'S', 's'].includes(prevType)) {
                    cp1X = currentX + (currentX - lastControlX);
                    cp1Y = currentY + (currentY - lastControlY);
                  } else {
                    cp1X = currentX;
                    cp1Y = currentY;
                  }

                  // x2 and y2 become the "last control point" for the NEXT command
                  nextLastControlX = x2;
                  nextLastControlY = y2;

                  currentX = x;
                  currentY = y;
                  break;
                }

                default:
                  i = args.length;
              }

              prevType = type;
              lastControlX = nextLastControlX ?? currentX;
              lastControlY = nextLastControlY ?? currentY;
              console.log(`Command: ${type},ID: ${im++} , Current Point: (${currentX}, ${currentY})`); // Debug log
              minX = Math.min(minX, currentX);
              minY = Math.min(minY, currentY);
              maxX = Math.max(maxX, currentX);
              maxY = Math.max(maxY, currentY);
            }
          });
        }
      }
    }

    if (node.children) {
      node.children.forEach(findBounds);
    }
  }

  findBounds(parsed);

  if (!isFinite(minX)) {
    minX = minY = 0;
    maxX = maxY = 1;
  }

  const svgWidth = maxX - minX;
  const svgHeight = maxY - minY;



  const selectionWidth = Math.abs(selectionArea.startX - selectionArea.endX);
  const selectionHeight = Math.abs(selectionArea.startY - selectionArea.endY);

  const scale = Math.min(
    svgWidth === 0 ? 1 : selectionWidth / svgWidth,
    svgHeight === 0 ? 1 : selectionHeight / svgHeight
  );

  const offsetX = Math.min(selectionArea.startX, selectionArea.endX) - 100; //+transform.translateX*transform.scale;
  const offsetY = Math.min(selectionArea.startY, selectionArea.endY) - 100; //+transform.translateY*transform.scale;

  /* =========================
     SHAPE CREATION
  ========================== */

  function traverse(node) {
    if (!node) return;

    if (node.type === 'element' && node.properties) {
      const props = node.properties;
      let shape = null;

      if (node.tagName === 'circle' || node.tagName === 'ellipse') {
        const props = node.properties;
        const cx = parseFloat(props.cx || 0);
        const cy = parseFloat(props.cy || 0);

        // Handle both circle (r) and ellipse (rx, ry)
        const rx = node.tagName === 'circle' ? parseFloat(props.r || 0) : parseFloat(props.rx || 0);
        const ry = node.tagName === 'circle' ? parseFloat(props.r || 0) : parseFloat(props.ry || 0);

        // 1. Get the 3 control points in SVG local space
        const localPoints = getThreePointsFromEllipse(cx, cy, rx, ry);

        // 2. Transform these 3 points into your application's coordinate space
        const transformedPoints = localPoints.map(p => ({
          x: (p.x - minX) * scale + offsetX,
          y: (p.y - minY) * scale + offsetY,
          type: 'line' // Type is less important here as Ellipse logic uses raw x/y
        }));

        // 3. Instantiate the class with exactly 3 points
        if (node.tagName === 'circle') {
          shape = new Circle(
            transformedPoints,
            props.stroke || 'black',
            props.fill || 'transparent',
            props.fillType || 'parallel',
            parseFloat(props.spacing) || 2,
            parseFloat(props.angle) || 45,
            parseFloat(props.segmentLength) || 5
          );
        } else {
          shape = new Ellipse(
            transformedPoints,
            props.stroke || 'black',
            props.fill || 'transparent',
            props.fillType || 'parallel',
            parseFloat(props.spacing) || 2,
            parseFloat(props.angle) || 45,
            parseFloat(props.segmentLength) || 5
          );
        }
      }

      if (node.tagName === 'path' && props.d) {
        const commands = props.d.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g) || [];
        const points = [];

        let currentX = 0;
        let currentY = 0;
        let startX = 0;
        let startY = 0;

        if (commands) {
          commands.forEach(command => {
            const type = command[0];
            const args =
              command.slice(1).match(numberRegex)?.map(Number) || [];

            if (type === 'z') {
              currentX = startX;
              currentY = startY;
              points.push({ x: currentX, y: currentY, type: 'line' });
            }

            let i = 0;

            while (i < args.length) {
              let nextLastControlX, nextLastControlY;
              switch (type) {

                case 'M':
                  currentX = args[i++];
                  currentY = args[i++];
                  startX = currentX;
                  startY = currentY;
                  points.push({ x: currentX, y: currentY, type: 'line' });
                  break;

                case 'm':
                  currentX += args[i++];
                  currentY += args[i++];
                  startX = currentX;
                  startY = currentY;
                  points.push({ x: currentX, y: currentY, type: 'line' });
                  break;

                case 'L':
                  currentX = args[i++];
                  currentY = args[i++];
                  points.push({ x: currentX, y: currentY, type: 'line' });
                  break;

                case 'l':
                  currentX += args[i++];
                  currentY += args[i++];
                  points.push({ x: currentX, y: currentY, type: 'line' });
                  break;

                case 'C': {
                  const p0 = { x: currentX, y: currentY };
                  const cp1 = { x: args[i++], y: args[i++] };
                  const cp2 = { x: args[i++], y: args[i++] };
                  const p3 = { x: args[i++], y: args[i++] };

                  const sampled = sampleCubic(p0, cp1, cp2, p3);
                  points.push(...sampled);

                  currentX = p3.x;
                  currentY = p3.y;
                  break;
                }

                case 'c': {
                  const p0 = { x: currentX, y: currentY };
                  const cp1 = { x: currentX + args[i++], y: currentY + args[i++] };
                  const cp2 = { x: currentX + args[i++], y: currentY + args[i++] };
                  const p3 = { x: currentX + args[i++], y: currentY + args[i++] };

                  const sampled = sampleCubic(p0, cp1, cp2, p3);
                  points.push(...sampled);

                  currentX = p3.x;
                  currentY = p3.y;
                  break;
                }

                case 'Z':
                case 'z':
                  currentX = startX;
                  currentY = startY;
                  points.push({ x: currentX, y: currentY, type: 'line' });
                  i = args.length;
                  break;
                case 'S':
                case 's': {
                  let x2 = args[i++];
                  let y2 = args[i++];
                  let x = args[i++];
                  let y = args[i++];

                  if (type === 's') {
                    x2 += currentX;
                    y2 += currentY;
                    x += currentX;
                    y += currentY;
                  }

                  // Calculate the mirrored reflection of the previous control point
                  let cp1X, cp1Y;
                  if (['C', 'c', 'S', 's'].includes(prevType)) {
                    cp1X = currentX + (currentX - lastControlX);
                    cp1Y = currentY + (currentY - lastControlY);
                  } else {
                    cp1X = currentX;
                    cp1Y = currentY;
                  }

                  // x2 and y2 become the "last control point" for the NEXT command
                  nextLastControlX = x2;
                  nextLastControlY = y2;

                  currentX = x;
                  currentY = y;
                  break;
                }


                default:
                  i = args.length;
              }
              prevType = type;
              lastControlX = nextLastControlX ?? currentX;
              lastControlY = nextLastControlY ?? currentY;
            }



          });
        }

        // Apply scaling & offset
        const transformed = points.map(p => ({
          x: (p.x - minX) * scale + offsetX,
          y: (p.y - minY) * scale + offsetY,
          type: p.type
        }));

        shape = new PolySpline(
          transformed,
          props.stroke || 'black',
          props.fill || 'transparent',
          props.fillType || 'parallel',
          parseFloat(props.spacing) || 2,
          parseFloat(props.angle) || 45,
          parseFloat(props.segmentLength) || 5
        );
      }

      if (shape) shapes.push(shape);
    }

    if (node.children) {
      node.children.forEach(traverse);
    }
  }

  traverse(parsed);
  return shapes;
}

export { parseSvg };
